import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ArrowDropDown } from '@mui/icons-material';
import Check from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Typography,
  Modal,
  IconButton,
  CardMedia,
  MenuItem,
  Grid,
  Divider,
  Popover,
  MenuList,
  Box,
  Button
} from '@mui/material';
import { updateDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getIcon } from '../../api/aws/s3';
import {
  negotiation as negotiationDoc,
  negotiationSnap
} from '../../api/firebase/firestore/negotiation';
import {
  userFindByCompanyId,
  userSnapshot
} from '../../api/firebase/firestore/user';
import {
  black,
  blueButton,
  primaryButton,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { screenType } from '../../constants/enum';
import { throwError } from '../../modules/error';
import noImageIcon from '../assets/noImageIcon.svg';

import type { NegotiationType } from '../../api/firebase/firestore/negotiation/negotiationType';
import type { UserType } from '../../api/firebase/firestore/user/userType';
import type { User } from 'firebase/auth';

const styles = {
  blueButton,
  modal: {
    '@media screen and (max-width:480px)': {
      margin: '0 16px'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: 2
  },
  paper: {
    '@media screen and (max-width:480px)': {
      padding: '24px 16px',
      width: '100%'
    },
    backgroundColor: 'white',
    maxHeight: '90%',
    overflowY: 'auto',
    padding: '24px 0',
    position: 'relative',
    width: '480px'
  },
  primaryButton,
  tertiaryButtonBackground
};

interface Props {
  open: boolean;
  handleClose: () => void;
  currentUser: User | null | undefined;
  screen: number;
  initChatList: () => Promise<void>;
  initMessage: () => Promise<void>;
  disableOrderApprove?: () => Promise<void>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const ShareModal = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const {
    handleClose,
    open,
    currentUser,
    screen,
    initChatList,
    initMessage,
    disableOrderApprove,
    setLoading
  } = props;
  const [anchorEl, setAnchorEl] = React.useState<Array<null | HTMLElement>>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [value, setValue] = useState<string[]>([]);
  const [type, setType] = useState<number[]>([]);
  const { id } = useParams<{ id: string }>();

  const submit = async (): Promise<void> => {
    setLoading(true);
    try {
      let agentMainUid = '';
      const agentUnauthorisedIds: string[] = [];
      const agentNoticeIds: string[] = [];
      let clientMainUid = '';
      const clientUnauthorisedIds: string[] = [];
      const clientNoticeIds: string[] = [];
      let publisherMainUid = '';
      const publisherUnauthorisedIds: string[] = [];
      const publisherNoticeIds: string[] = [];

      users.forEach((user, index) => {
        if (user.type === screenType.client) {
          if (value[index] === 'option1') {
            clientMainUid = user.uid;
          } else if (value[index] === 'option2') {
            clientNoticeIds.push(user.uid);
          } else if (value[index] === 'option4') {
            clientUnauthorisedIds.push(user.uid);
          }
        } else if (user.type === screenType.publisher) {
          if (value[index] === 'option1') {
            publisherMainUid = user.uid;
          } else if (value[index] === 'option2') {
            publisherNoticeIds.push(user.uid);
          } else if (value[index] === 'option4') {
            publisherUnauthorisedIds.push(user.uid);
          }
        } else if (user.type === screenType.agent) {
          if (value[index] === 'option1') {
            agentMainUid = user.uid;
          } else if (value[index] === 'option2') {
            agentNoticeIds.push(user.uid);
          } else if (value[index] === 'option4') {
            agentUnauthorisedIds.push(user.uid);
          }
        }
      });

      const negotiationRef = negotiationDoc(id ?? '');
      if (screen === screenType.client) {
        await updateDoc(negotiationRef, {
          clientMainUid,
          clientMember: clientNoticeIds,
          clientUnauthorizedMember: clientUnauthorisedIds
        });
      } else if (screen === screenType.agent) {
        await updateDoc(negotiationRef, {
          agentMainUid,
          agentMember: agentNoticeIds,
          agentUnauthorizedMember: agentUnauthorisedIds
        });
      } else if (screen === screenType.publisher) {
        await updateDoc(negotiationRef, {
          publisherMainUid,
          publisherMember: publisherNoticeIds,
          publisherUnauthorizedMember: publisherUnauthorisedIds
        });
      }
    } catch (error) {
      throwError(error);
    } finally {
      await initChatList();
      await initMessage();
      if (disableOrderApprove) {
        await disableOrderApprove();
      }
      setLoading(false);
      handleClose();
    }
  };

  const init = async (): Promise<void> => {
    try {
      const user = await userSnapshot(currentUser?.uid ?? '');
      if (user) {
        const usersQuery = await userFindByCompanyId(
          (user.data()?.companyId as string) ?? ''
        );
        const usersWithIcons = await Promise.all(
          usersQuery.map(async (u): Promise<UserType> => {
            const iconUrl = await getIcon(u);
            const userData = u.data() as UserType;
            return { ...userData, icon: iconUrl };
          })
        );
        const negotiation = await negotiationSnap(id ?? '');

        if (screen === screenType.client || screen === screenType.agent) {
          const publishersQuery = await userFindByCompanyId(
            (negotiation?.data() as NegotiationType)?.publisherId ?? ''
          );
          const publishersWithIcons = await Promise.all(
            publishersQuery.map(async (u): Promise<UserType> => {
              const iconUrl = await getIcon(u);
              const userData = u.data() as UserType;
              return { ...userData, icon: iconUrl };
            })
          );
          const clientUnauthorisedIds =
            ((negotiation?.data() as NegotiationType)
              ?.clientUnauthorizedMember as string[]) ?? [];

          const agentUnauthorisedIds =
            ((negotiation?.data() as NegotiationType)
              ?.agentUnauthorizedMember as string[]) ?? [];
          const publisherUnauthorisedIds =
            ((negotiation?.data() as NegotiationType)
              ?.publisherUnauthorizedMember as string[]) ?? [];

          const filteredUsers = publishersWithIcons.filter(
            (u) => !publisherUnauthorisedIds.includes(u.uid)
          );

          const publisherMainUid = (negotiation?.data() as NegotiationType)
            ?.publisherMainUid;

          const clientMainUid = (negotiation?.data() as NegotiationType)
            ?.clientMainUid;

          const agentMainUid = (negotiation?.data() as NegotiationType)
            ?.agentMainUid;

          const publisherNoticeIds =
            ((negotiation?.data() as NegotiationType)
              ?.publisherMember as string[]) ?? [];

          const clientNoticeIds =
            ((negotiation?.data() as NegotiationType)
              ?.clientMember as string[]) ?? [];
          const agentNoticeIds =
            ((negotiation?.data() as NegotiationType)
              ?.agentMember as string[]) ?? [];

          setUsers([...usersWithIcons, ...filteredUsers]);

          setValue(
            [...usersWithIcons, ...filteredUsers].map((u) => {
              if (
                u.uid === publisherMainUid ||
                u.uid === clientMainUid ||
                u.uid === agentMainUid
              ) {
                return 'option1';
              } else if (
                publisherNoticeIds.includes(u.uid) ||
                clientNoticeIds.includes(u.uid) ||
                agentNoticeIds.includes(u.uid)
              ) {
                return 'option2';
              } else if (
                publisherUnauthorisedIds.includes(u.uid) ||
                clientUnauthorisedIds.includes(u.uid) ||
                agentUnauthorisedIds.includes(u.uid)
              ) {
                return 'option4';
              }
              return 'option3';
            })
          );
          setType(
            [...usersWithIcons, ...filteredUsers].map((u) => {
              if (u.type === screenType.client) {
                return screenType.client;
              } else if (u.type === screenType.agent) {
                return screenType.agent;
              } else if (u.type === screenType.publisher) {
                return screenType.publisher;
              }
              return 0;
            })
          );
        } else if (screen === screenType.publisher) {
          const clientsQuery = await userFindByCompanyId(
            (negotiation?.data() as NegotiationType)?.clientId ?? ''
          );
          const agentsQuery = await userFindByCompanyId(
            (negotiation?.data() as NegotiationType)?.agentId ?? ''
          );
          const clientsWithIcons = await Promise.all(
            clientsQuery.map(async (u): Promise<UserType> => {
              const iconUrl = await getIcon(u);
              const userData = u.data() as UserType;
              return { ...userData, icon: iconUrl };
            })
          );

          const clientUnauthorisedIds =
            ((negotiation?.data() as NegotiationType)
              ?.clientUnauthorizedMember as string[]) ?? [];

          const agentsWithIcons = await Promise.all(
            agentsQuery.map(async (u): Promise<UserType> => {
              const iconUrl = await getIcon(u);
              const userData = u.data() as UserType;
              return { ...userData, icon: iconUrl };
            })
          );

          const agentUnauthorisedIds =
            ((negotiation?.data() as NegotiationType)
              ?.agentUnauthorizedMember as string[]) ?? [];

          const publisherUnauthorisedIds =
            ((negotiation?.data() as NegotiationType)
              ?.publisherUnauthorizedMember as string[]) ?? [];

          const publisherMainUid = (negotiation?.data() as NegotiationType)
            ?.publisherMainUid;
          const publisherNoticeIds =
            ((negotiation?.data() as NegotiationType)
              ?.publisherMember as string[]) ?? [];

          const clientMainUid = (negotiation?.data() as NegotiationType)
            ?.clientMainUid;
          const clientNoticeIds =
            ((negotiation?.data() as NegotiationType)
              ?.clientMember as string[]) ?? [];

          const agentMainUid = (negotiation?.data() as NegotiationType)
            ?.agentMainUid;
          const agentNoticeIds =
            ((negotiation?.data() as NegotiationType)
              ?.agentMember as string[]) ?? [];

          let filteredClientAgentUsers: UserType[] = [];
          filteredClientAgentUsers = filteredClientAgentUsers.concat(
            clientsWithIcons.filter(
              (u) => !clientUnauthorisedIds.includes(u.uid)
            )
          );
          filteredClientAgentUsers = filteredClientAgentUsers.concat(
            agentsWithIcons.filter((u) => !agentUnauthorisedIds.includes(u.uid))
          );
          setUsers([...usersWithIcons, ...filteredClientAgentUsers]);
          setValue(
            [...usersWithIcons, ...filteredClientAgentUsers].map((u) => {
              if (
                u.uid === clientMainUid ||
                u.uid === agentMainUid ||
                u.uid === publisherMainUid
              ) {
                return 'option1';
              } else if (
                clientNoticeIds.includes(u.uid) ||
                agentNoticeIds.includes(u.uid) ||
                publisherNoticeIds.includes(u.uid)
              ) {
                return 'option2';
              } else if (
                clientUnauthorisedIds.includes(u.uid) ||
                agentUnauthorisedIds.includes(u.uid) ||
                publisherUnauthorisedIds.includes(u.uid)
              ) {
                return 'option4';
              }
              return 'option3';
            })
          );
          setType(
            [...usersWithIcons, ...filteredClientAgentUsers].map((u) => {
              if (u.type === screenType.client) {
                return screenType.client;
              } else if (u.type === screenType.agent) {
                return screenType.agent;
              } else if (u.type === screenType.publisher) {
                return screenType.publisher;
              }
              return 0;
            })
          );
        }
      }
    } catch (error) {
      throwError(error);
    }
  };

  const checkOption1Count = (): boolean => {
    if (screen === screenType.client) {
      const count = value.filter(
        (item, index) => item === 'option1' && type[index] === screenType.client
      ).length;
      if (count <= 0 || count >= 2) {
        return true;
      }
      return false;
    } else if (screen === screenType.agent) {
      const count = value.filter(
        (item, index) => item === 'option1' && type[index] === screenType.agent
      ).length;
      if (count <= 0 || count >= 2) {
        return true;
      }
      return false;
    } else if (screen === screenType.publisher) {
      const count = value.filter(
        (item, index) =>
          item === 'option1' && type[index] === screenType.publisher
      ).length;
      if (count <= 0 || count >= 2) {
        return true;
      }
      return false;
    }
    return false;
  };

  useEffect(() => {
    void (async () => {
      await init();
    })();
  }, [id, open]);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ): void => {
    const newAnchorEl = [...anchorEl];
    newAnchorEl[index] = event.currentTarget;
    setAnchorEl(newAnchorEl);
  };

  const handleClosePopover = (index: number): void => {
    const newAnchorEl = [...anchorEl];
    newAnchorEl[index] = null;
    setAnchorEl(newAnchorEl);
  };

  const handleItemClick = (option: string, index: number): void => {
    const newValue = [...value];
    newValue[index] = option;
    setValue(newValue);
    handleClosePopover(index);
  };

  const getDisplayText = (val: string): string => {
    switch (val) {
      case 'option1':
        return '主担当者';
      case 'option2':
        return '通知あり';
      case 'option3':
        return '通知なし';
      case 'option4':
        return '権限を削除';
      default:
        return '';
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={async () => {
          handleClose();
          await init();
        }}
        sx={styles.modal}
      >
        <Typography sx={styles.paper}>
          <Typography
            sx={{
              '@media screen and (max-width:480px)': {
                p: 0
              },
              p: '0 40px'
            }}
          >
            <IconButton
              edge="end"
              color="inherit"
              aria-label="close"
              onClick={async () => {
                handleClose();
                await init();
              }}
              style={{
                color: '#757575',
                position: 'absolute',
                right: '40px',
                top: '24px'
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" gutterBottom sx={{ color: black }}>
              {t('message.messageShare')}
            </Typography>
            {users.map((user, index) => (
              <Typography key={index}>
                <Grid container alignItems="center">
                  <Grid item>
                    <CardMedia
                      component="img"
                      image={user.icon ?? noImageIcon}
                      sx={{
                        borderRadius: '50%',
                        height: '32px',
                        objectFit: 'cover',
                        width: '32px'
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Typography
                      sx={{
                        color: black,
                        fontSize: '14px',
                        ml: '8px',
                        mr: '4px'
                      }}
                    >
                      {user.name}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography sx={{ color: '#808A9F', fontSize: '12px' }}>
                      {user.type === screenType.client
                        ? '広告主'
                        : user.type === screenType.publisher
                        ? '媒体社'
                        : '代理店'}
                    </Typography>
                  </Grid>
                  {user.type === screen && (
                    <Grid
                      item
                      sx={{
                        display: 'flex',
                        flexGrow: 1,
                        justifyContent: 'flex-end'
                      }}
                    >
                      <Grid item>
                        <Typography
                          component="span"
                          onClick={(e) => handleClick(e, index)}
                          sx={{
                            alignItems: 'center',
                            cursor: 'pointer',
                            display: 'flex'
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                value[index] === 'option4' ? '#B3B3B3' : black,
                              fontSize: '14px'
                            }}
                          >
                            {getDisplayText(value[index])}
                          </Typography>
                          <ArrowDropDown />
                        </Typography>
                        <Popover
                          open={Boolean(anchorEl[index])}
                          anchorEl={anchorEl[index]}
                          onClose={handleClosePopover}
                          anchorOrigin={{
                            horizontal: 'center',
                            vertical: 'bottom'
                          }}
                          transformOrigin={{
                            horizontal: 'center',
                            vertical: 'top'
                          }}
                        >
                          <MenuList>
                            <MenuItem
                              value="option1"
                              selected={value[index] === 'option1'}
                              onClick={() => handleItemClick('option1', index)}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  justifyContent: 'space-between',
                                  p: '0 16px',
                                  width: '100%'
                                }}
                              >
                                <Typography>
                                  {value[index] === 'option1' && (
                                    <Check
                                      sx={{ fill: '#0A7EBF', mr: '16px' }}
                                    />
                                  )}
                                </Typography>
                                <Typography>主担当者</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem
                              value="option2"
                              selected={value[index] === 'option2'}
                              onClick={() => handleItemClick('option2', index)}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  justifyContent: 'space-between',
                                  p: '0 16px',
                                  width: '100%'
                                }}
                              >
                                <Typography>
                                  {value[index] === 'option2' && (
                                    <Check
                                      sx={{ fill: '#0A7EBF', mr: '16px' }}
                                    />
                                  )}
                                </Typography>
                                <Typography>通知あり</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem
                              value="option3"
                              selected={value[index] === 'option3'}
                              onClick={() => handleItemClick('option3', index)}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  justifyContent: 'space-between',
                                  p: '0 16px',
                                  width: '100%'
                                }}
                              >
                                <Typography>
                                  {value[index] === 'option3' && (
                                    <Check
                                      sx={{ fill: '#0A7EBF', mr: '16px' }}
                                    />
                                  )}
                                </Typography>
                                <Typography>通知なし</Typography>
                              </Box>
                            </MenuItem>
                            <Divider />
                            <MenuItem
                              value="option4"
                              selected={value[index] === 'option4'}
                              onClick={() => handleItemClick('option4', index)}
                            >
                              <Box
                                sx={{
                                  p: '0 16px',
                                  width: '100%'
                                }}
                              >
                                <Typography></Typography>
                                <Typography>権限を削除</Typography>
                              </Box>
                            </MenuItem>
                          </MenuList>
                        </Popover>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Divider sx={{ mb: '8px', mt: '8px' }} />
              </Typography>
            ))}
            <Typography>
              <Button
                disabled={checkOption1Count()}
                fullWidth
                sx={[styles.primaryButton, { mt: '24px' }]}
                onClick={async () => {
                  await submit();
                }}
              >
                完了
              </Button>
            </Typography>
          </Typography>
        </Typography>
      </Modal>
    </>
  );
};
export default ShareModal;
